import { getVerificationSessions } from "@/api/identity-verification";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import useGetUserInformation from "@/features/auth/useGetUserInformation";
import {
	CLEAR_APPLICATION_ERROR_MESSAGE,
	clearPendingClearSession,
	loadPendingClearSession,
} from "@/features/identity-verification/pendingClearSession";
import { useLoaderData, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

const VALIDATE_IDENTITY_PATH = "/main/validate-identity";

/**
 * The guest came back to Kashu from a CLEAR session without CLEAR redirecting them (its page
 * failed, or they left it): asks the API for the session status, once, when the logged-in app
 * loads, and shows CLEAR's error if the session is still awaiting_user_input.
 */
export default function ClearSessionErrorCheck() {
	const guestId = useGetUserInformation().data?.id;
	const { location_id } = useLoaderData({ from: "__root__" });
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const ranRef = useRef(false);
	const [showError, setShowError] = useState(false);

	useEffect(() => {
		if (ranRef.current || !guestId) return;
		// CLEAR's redirect lands here: the validate page reads the session itself.
		if (pathname.startsWith(VALIDATE_IDENTITY_PATH)) return;
		ranRef.current = true;

		const pending = loadPendingClearSession();
		if (!pending) return;
		// Another guest's session on this device is theirs to see.
		if (pending.guestId !== guestId) {
			clearPendingClearSession();
			return;
		}

		getVerificationSessions({
			guest_id: guestId,
			user_id: guestId,
			location_id,
		})
			.then((session) => {
				clearPendingClearSession();
				if (session.status === "awaiting_user_input") setShowError(true);
			})
			.catch((error) => {
				// No answer: kept for the next load. Any API answer is the outcome.
				if ((error as { http_status?: number } | null)?.http_status !== 0) {
					clearPendingClearSession();
				}
			});
	}, [guestId, location_id, pathname]);

	return (
		<Dialog open={showError} onOpenChange={setShowError}>
			<DialogContent className="w-[calc(100%-48px)] rounded-3xl px-8 pt-14 pb-8 [&>button]:right-5 [&>button]:top-5">
				<DialogHeader>
					<DialogTitle>Identity verification not completed</DialogTitle>
					<DialogDescription>
						{CLEAR_APPLICATION_ERROR_MESSAGE}
					</DialogDescription>
				</DialogHeader>
			</DialogContent>
		</Dialog>
	);
}
